import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSearch,
  faChevronUp,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faList
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchSystemCaregiverById,
  fetchSystemCaregiverPatients,
  clearError
} from '../../../store/slices/systemCaregiverSlice';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext'; // Import Breadcrumb Hook
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import './SystemCaregiverDetails.css';

const SystemCaregiverDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { caregiverId } = useParams();
  const { updateBreadcrumbs } = useBreadcrumb(); // Use Breadcrumb Hook
  const {
    currentSystemCaregiver,
    patients,
    patientsPagination,
    isLoading,
    patientsLoading,
    error
  } = useSelector(state => state.systemCaregiver);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Update Breadcrumbs
  useEffect(() => {
    const caregiverName = currentSystemCaregiver
      ? `${currentSystemCaregiver.last_name || ''}, ${currentSystemCaregiver.first_name || ''}`.trim() || currentSystemCaregiver.name || 'System Caregiver Details'
      : 'System Caregiver Details';

    updateBreadcrumbs([
      { label: 'System Caregivers', path: '/system-caregiver-dashboard' },
      { label: caregiverName, path: `/system-caregiver-dashboard/${caregiverId}`, active: true }
    ]);
  }, [currentSystemCaregiver, caregiverId, updateBreadcrumbs]);

  useEffect(() => {
    if (caregiverId) {
      dispatch(fetchSystemCaregiverById(caregiverId)).catch((err) => {
        console.error('Error fetching system caregiver:', err);
      });
    }
  }, [dispatch, caregiverId]);

  useEffect(() => {
    if (caregiverId) {
      const params = {
        page: currentPage,
        limit: entriesPerPage === 'all' ? 1000 : parseInt(entriesPerPage),
        search: searchTerm
      };
      dispatch(fetchSystemCaregiverPatients({ caregiverId, params }));
    }
  }, [dispatch, caregiverId, currentPage, entriesPerPage, searchTerm]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= patientsPagination.total_pages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  // Sorting functionality
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted patients
  const getSortedPatients = () => {
    if (!sortColumn) return patients;

    const sorted = [...patients].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined || aValue === 'N/A') aValue = '';
      if (bValue === null || bValue === undefined || bValue === 'N/A') bValue = '';

      // Handle numeric values
      if (sortColumn === 'sr' || sortColumn === 'id') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      }

      // Compare values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return sorted;
  };

  const sortedPatients = getSortedPatients();

  // Get sort icon for column
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FontAwesomeIcon icon={faChevronUp} className="sort-icon" />;
    }
    return sortDirection === 'asc'
      ? <FontAwesomeIcon icon={faChevronUp} className="sort-icon active" />
      : <FontAwesomeIcon icon={faChevronDown} className="sort-icon active" />;
  };

  const getStatusBadge = (status) => {
    if (status === 'active' || status === 'Active') {
      return <span className="badge bg-success">Active</span>;
    } else if (status === 'inactive' || status === 'Inactive') {
      return <span className="badge bg-warning">Inactive</span>;
    } else {
      return <span className="badge bg-secondary">N/A</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="app-page-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !currentSystemCaregiver) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          {error || 'System caregiver not found'}
        </div>
        <button
          className="btn btn-primary mt-3"
          onClick={() => navigate('/system-caregiver-dashboard')}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to System Caregiver Dashboard
        </button>
      </div>
    );
  }

  if (!currentSystemCaregiver && !isLoading) {
    return (
      <div className="container-fluid">
        <div className="alert alert-warning" role="alert">
          System caregiver not found. Please check the caregiver ID.
        </div>
        <button
          className="btn btn-primary mt-3"
          onClick={() => navigate('/system-caregiver-dashboard')}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to System Caregiver Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="scd-container">
      {/* Header */}
      <div className="scd-header">
        {/* Heading removed as per request */}
        <div className="scd-header-actions">
          <button
            type="button"
            className="scd-events-log-btn"
            onClick={() => navigate(`/event-logs/System%20Caregiver/${caregiverId}`)}
            title="View Change History"
          >
            <FontAwesomeIcon icon={faList} className="me-2" />
            Event Logs
          </button>
          <button
            className="scd-back-btn"
            onClick={() => navigate('/system-caregiver-dashboard')}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </button>
        </div>
      </div>

      {/* System Caregiver Details Card */}
      <div className="scd-details-card">
        <div className="scd-top-header">
          <h5>System Caregiver Details</h5>
        </div>

        <div className="scd-info-grid">
          {/* Column 1 */}
          <div className="scd-info-column">
            <div className="scd-info-row">
              <span className="scd-label">Name:</span>
              <span className="scd-value">
                {currentSystemCaregiver ? `${currentSystemCaregiver.last_name || ''}, ${currentSystemCaregiver.first_name || ''}`.trim() : 'N/A'}
              </span>
            </div>
            <div className="scd-info-row">
              <span className="scd-label">Username:</span>
              <span className="scd-value">{currentSystemCaregiver?.username || 'N/A'}</span>
            </div>
            <div className="scd-info-row">
              <span className="scd-label">Email:</span>
              <span className="scd-value">{currentSystemCaregiver?.email || 'N/A'}</span>
            </div>
          </div>

          {/* Column 2 */}
          <div className="scd-info-column">
            <div className="scd-info-row">
              <span className="scd-label">Home Phone:</span>
              <span className="scd-value">{formatPhoneForDisplay(currentSystemCaregiver?.phone, 'N/A')}</span>
            </div>
            <div className="scd-info-row">
              <span className="scd-label">Cell Phone:</span>
              <span className="scd-value">{formatPhoneForDisplay(currentSystemCaregiver?.cell_phone, 'N/A')}</span>
            </div>
            <div className="scd-info-row">
              <span className="scd-label">Fax:</span>
              <span className="scd-value">{formatPhoneForDisplay(currentSystemCaregiver?.fax, 'N/A')}</span>
            </div>
            <div className="scd-info-row">
              <span className="scd-label">Status:</span>
              <span className="scd-value">
                <span className={`scd-status-badge ${currentSystemCaregiver?.status === 'active' || currentSystemCaregiver?.status === 'Active'
                  ? 'scd-status-active'
                  : 'scd-status-inactive'
                  }`}>
                  {currentSystemCaregiver?.status || 'N/A'}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Patient List Section */}
      <div className="scd-table-card">
        <div className="scd-table-header">
          <h5 className="scd-section-title">Patient List</h5>
          <div className="scd-search-box">
            <input
              type="text"
              className="scd-search-input"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <FontAwesomeIcon icon={faSearch} className="scd-search-icon" />
          </div>
        </div>

        {patientsLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="scd-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('sr')}>
                      Sr. <FontAwesomeIcon
                        icon={sortColumn === 'sr' && sortDirection === 'asc' ? faChevronUp : faChevronDown}
                        className={`scd-sort-icon ${sortColumn === 'sr' ? 'active' : ''}`}
                      />
                    </th>
                    <th onClick={() => handleSort('patient_name')}>
                      Patient Name <FontAwesomeIcon
                        icon={sortColumn === 'patient_name' && sortDirection === 'asc' ? faChevronUp : faChevronDown}
                        className={`scd-sort-icon ${sortColumn === 'patient_name' ? 'active' : ''}`}
                      />
                    </th>
                    <th onClick={() => handleSort('practice_name')}>
                      Practice Name <FontAwesomeIcon
                        icon={sortColumn === 'practice_name' && sortDirection === 'asc' ? faChevronUp : faChevronDown}
                        className={`scd-sort-icon ${sortColumn === 'practice_name' ? 'active' : ''}`}
                      />
                    </th>
                    <th onClick={() => handleSort('provider_name')}>
                      Provider Name <FontAwesomeIcon
                        icon={sortColumn === 'provider_name' && sortDirection === 'asc' ? faChevronUp : faChevronDown}
                        className={`scd-sort-icon ${sortColumn === 'provider_name' ? 'active' : ''}`}
                      />
                    </th>
                    <th onClick={() => handleSort('contact')}>
                      Contact <FontAwesomeIcon
                        icon={sortColumn === 'contact' && sortDirection === 'asc' ? faChevronUp : faChevronDown}
                        className={`scd-sort-icon ${sortColumn === 'contact' ? 'active' : ''}`}
                      />
                    </th>
                    <th onClick={() => handleSort('email')}>
                      Email <FontAwesomeIcon
                        icon={sortColumn === 'email' && sortDirection === 'asc' ? faChevronUp : faChevronDown}
                        className={`scd-sort-icon ${sortColumn === 'email' ? 'active' : ''}`}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPatients.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <p className="text-muted mb-0">No patients found</p>
                      </td>
                    </tr>
                  ) : (
                    sortedPatients.map((patient, index) => (
                      <tr key={patient.id || index}>
                        <td>{patient.sr || ((currentPage - 1) * parseInt(entriesPerPage) + index + 1)}</td>
                        <td>{patient.patient_name || 'N/A'}</td>
                        <td>{patient.practice_name || 'N/A'}</td>
                        <td>{patient.provider_name || 'N/A'}</td>
                        <td>{patient.contact || 'N/A'}</td>
                        <td>{patient.email || 'N/A'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {sortedPatients.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center">
                  <label className="form-label me-2 mb-0">Show</label>
                  <select
                    className="form-select form-select-sm d-inline-block"
                    style={{ width: '80px' }}
                    value={entriesPerPage}
                    onChange={(e) => handleEntriesChange(e.target.value)}
                  >
                    <option value="30">30</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="all">All</option>
                  </select>
                  <span className="ms-2">entries</span>
                </div>
                <div className="d-flex align-items-center">
                  <span className="me-2">Page {patientsPagination.current_page} of {patientsPagination.total_pages}</span>
                  <button
                    className="btn btn-sm btn-outline-secondary me-1"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === patientsPagination.total_pages}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SystemCaregiverDetails;

